import React, { useEffect } from 'react'
import { Card, Divider, Box, Button } from '@mui/material'
import { MapControl, ControlPosition } from '@vis.gl/react-google-maps'
import { Icon, TypoGraph } from 'components'
import { formatDate, formatEventTypeLabel } from 'tools/helper'
import { VehicleEvent } from 'interfaces/IClaimsSearching'

interface ClaimsMapEventSummaryProps {
  carrierName: string
  position: ControlPosition
  eventData: VehicleEvent
  handleOpenDetails: (open: boolean) => void
  onEventSelect: (
    eventId: number | null,
    openDetails?: boolean,
    showRoute?: boolean
  ) => void
}

const ClaimsMapEventSummary: React.FC<ClaimsMapEventSummaryProps> = ({
  carrierName,
  position,
  eventData,
  handleOpenDetails,
  onEventSelect,
}) => {

  const getDriverName = (
    driverFirstName: string,
    driverLastName: string,
    driverName: string
  ) => {
    return driverFirstName
      ? driverFirstName +
          (driverLastName ? ' ' + driverLastName.charAt(0) + '.' : '')
      : driverName
      ? driverName
      : 'Unknown'
  }

  const getCarrierNameWithoutCode = (carrierName: string) => {
    return carrierName.split('(')[0].trim()
  }

  return (
    <MapControl position={position}>
      <Card
        square
        sx={{
          overflow: 'hidden',
          width: '400px',
          margin: '10px',
        }}
      >
        <Box
          sx={{
            padding: '16px',
            position: 'relative',
            backgroundColor: '#e3f2fd',
          }}
        >
          {/* Header Section */}
          <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <TypoGraph
                content={formatEventTypeLabel(eventData.type)}
                variant="h2"
                sx={{
                  fontWeight: 300,
                  color: 'text.primary',
                  mb: 0.5,
                }}
              />
              <TypoGraph
                content={`Asset ID: ${eventData.vehicleId?.assetEldId}`}
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              />
            </Box>
            <Button
              onClick={() => onEventSelect(null)}
              sx={{
                minWidth: 'auto',
                width: '24px',
                height: '24px',
                padding: '0',
                margin: '0',
                backgroundColor: 'transparent',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&:active': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <Icon materialIcon="Close" color="primary" size={18} />
            </Button>
          </Box>

          {/* Event Details Section */}
          <Box sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ marginRight: '8px' }}>
                <Icon materialIcon="LocalShipping" color="text.secondary" />
              </Box>
              <TypoGraph
                content={`Fleet: ${getCarrierNameWithoutCode(
                  eventData.carrierName || carrierName
                )}`}
                variant="body2"
              />
            </Box>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ marginRight: '8px' }}>
                <Icon materialIcon="Sensors" color="text.secondary" />
              </Box>
              <TypoGraph
                content={`TSP: ${eventData.eldProvider}`}
                variant="body2"
              />
            </Box>
            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ marginRight: '8px' }}>
                <Icon materialIcon="NearMe" color="text.secondary" />
              </Box>
              <TypoGraph
                content={`VIN: ${eventData.vin || '-'}`}
                variant="body2"
              />
            </Box>

            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ marginRight: '8px' }}>
                <Icon materialIcon="Schedule" color="text.secondary" />
              </Box>
              <TypoGraph
                content={`Time: ${formatDate(eventData.time, true)}`}
                variant="body2"
              />
            </Box>

            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ marginRight: '8px' }}>
                <Icon materialIcon="Person" color="text.secondary" />
              </Box>
              <TypoGraph
                content={`Driver: ${getDriverName(
                  eventData.driverFirstName,
                  eventData.driverLastName,
                  eventData.driverName
                )}`}
                variant="body2"
              />
            </Box>
          </Box>

          {/* Footer Section */}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              color="primary"
              onClick={() => handleOpenDetails(true)}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </MapControl>
  )
}

export default ClaimsMapEventSummary
